/**
 * FirstDocCell — 排行榜/表格中"首次文档"列的统一渲染
 *
 * 此前在 App.jsx 内有 4 处重复实现（Pipeline / Trending / EarlyRadar×2），
 * 每处 ~30 行，渲染逻辑分歧（fallback 颜色、tooltip）。此组件统一 4 个状态：
 *   1. querying — 正在 Wayback 探测，显示 ···
 *   2. probed-but-empty — 已探测确认无文档，显示 N/A badge
 *   3. not-checked — 尚未查询，显示 —
 *   4. has-doc — 显示日期 + 来源 badge + 距今多久
 *
 * 同时收容 DocSourceBadge + DOC_SOURCE_META（仅被 FirstDocCell 使用）。
 */
import React from "react";

// ── 文档来源徽章元数据 ───────────────────────────────────────────────────
const DOC_SOURCE_META = {
  "gitbook":         { label: "GitBook",    color: "#3884ff", bg: "rgba(56,132,255,0.10)" },
  "mintlify":        { label: "Mintlify",   color: "#22c55e", bg: "rgba(34,197,94,0.10)" },
  "github-pages":    { label: "GH Pages",   color: "#6366f1", bg: "rgba(99,102,241,0.10)" },
  "github-readme":   { label: "README",     color: "#a78bfa", bg: "rgba(167,139,250,0.12)" },
  "notion":          { label: "Notion",     color: "#374151", bg: "rgba(55,65,81,0.10)" },
  "readme-io":       { label: "Readme.io",  color: "#018fb1", bg: "rgba(1,143,177,0.10)" },
  "docusaurus":      { label: "Docusaurus", color: "#2e8555", bg: "rgba(46,133,85,0.10)" },
  "docs-subdomain":  { label: "docs.*",     color: "#0ea5e9", bg: "rgba(14,165,233,0.10)" },
  "docs-path":       { label: "/docs",      color: "#0ea5e9", bg: "rgba(14,165,233,0.10)" },
  "whitepaper":      { label: "WP",         color: "#d97706", bg: "rgba(217,119,6,0.10)" },
  "learn-subdomain": { label: "Learn",      color: "#10b981", bg: "rgba(16,185,129,0.10)" },
  "website":         { label: "Site",       color: "#64748b", bg: "rgba(100,116,139,0.10)" },
  "wayback":         { label: "Wayback",    color: "#64748b", bg: "rgba(100,116,139,0.10)" },
  "no_doc_found":    { label: "N/A",        color: "#6b7280", bg: "rgba(107,114,128,0.08)" },
  "blog":            { label: "Blog",       color: "#8b5cf6", bg: "rgba(139,92,246,0.10)" },
  "unknown":         { label: "?",          color: "#9ca3af", bg: "rgba(156,163,175,0.10)" },
};

export function DocSourceBadge({ sourceType, source, url }) {
  const meta = DOC_SOURCE_META[sourceType] || DOC_SOURCE_META.unknown;
  const tooltip = `${source || meta.label}${url ? " · " + url.replace(/^https?:\/\//, "").slice(0, 60) : ""}`;
  return (
    <span
      title={tooltip}
      style={{
        display: "inline-block",
        fontSize: 8,
        fontWeight: 700,
        letterSpacing: 0.3,
        padding: "1px 5px",
        borderRadius: 3,
        marginLeft: 4,
        color: meta.color,
        background: meta.bg,
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      {meta.label}
    </span>
  );
}

/**
 * 从已分析项目对象抽取 firstDoc entry（用于 Watchlist/Portfolio）
 *
 * 三种返回：
 *   - { date, source, sourceType, url }  — 有文档
 *   - { sourceType: 'no_doc_found', ... } — 已探测但无文档（让 UI 显示 N/A）
 *   - null — 未探测
 */
export function extractFirstDoc(project) {
  if (!project?.timeline) return null;
  const entry = project.timeline.find((t) => t.key === "firstDoc");
  if (!entry) return null;
  if (entry.sourceType === "no_doc_found") {
    return { sourceType: "no_doc_found", source: entry.source || "Wayback", date: null, url: null };
  }
  if (!entry.date) return null;
  return {
    date: entry.date,
    source: entry.source,
    sourceType: entry.sourceType,
    url: entry.url,
  };
}

/**
 * FirstDocCell — 排行榜/表格中"首次文档"列的渲染
 *
 * 4 态：
 *   - querying=true              → ···（正在探测）
 *   - !entry && probed=true     → N/A badge（已探测但确认无）
 *   - !entry                     → —（尚未查询）
 *   - entry 有效                 → 📄 date + badge + age
 *
 * Props:
 *   - entry: { date, source, sourceType, url } | null
 *   - querying: bool       — 后台 Wayback 探测中
 *   - probed: bool         — 已探测但无结果（明确区别于"未探测"）
 *   - lang: 'zh' | 'en'    — 文案语言
 *   - mutedColor: string   — 未探测态文字颜色（默认 #94a3b8）
 *   - emptyColor: string   — "—" fallback 颜色（默认与 mutedColor 一致）
 */
export function FirstDocCell({
  entry,
  querying = false,
  probed = false,
  lang = "zh",
  mutedColor = "#94a3b8",
  emptyColor,
}) {
  const isEn = lang === "en";
  const emptyC = emptyColor || mutedColor;

  // 1. 正在探测
  if (querying) {
    return (
      <span
        style={{ color: mutedColor, fontSize: 10 }}
        title={isEn ? "Searching Wayback Machine..." : "正在查询 Wayback Machine..."}
      >
        ···
      </span>
    );
  }

  // 2/4 已探测但无结果 — 通过 probed 标记 或 entry.sourceType === 'no_doc_found'
  const isProbedEmpty =
    (!entry && probed) ||
    (entry && (entry.sourceType === "no_doc_found" || !entry.date));
  if (isProbedEmpty) {
    return (
      <span
        style={{ color: "#94a3b8", fontSize: 10 }}
        title={isEn ? "No public docs found via Wayback Machine" : "该项目暂无可被 Wayback Machine 收录的公开文档"}
      >
        <DocSourceBadge sourceType="no_doc_found" source="Wayback" /> {isEn ? "N/A" : "暂无"}
      </span>
    );
  }

  // 3. 尚未查询
  if (!entry) {
    return (
      <span
        style={{ color: emptyC, fontSize: 10 }}
        title={isEn ? "Not yet checked" : "尚未查询"}
      >
        —
      </span>
    );
  }

  // 4. 有文档
  const days = Math.floor((Date.now() - new Date(entry.date).getTime()) / 86400000);
  const ageColor =
    days < 90 ? "#dc2626" :
    days < 365 ? "#d97706" :
    days < 730 ? "#3b82f6" :
    "#6b7280";
  const ageLabel =
    days < 30 ? `${days}d` :
    days < 365 ? `${Math.round(days / 30)}mo` :
    `${(days / 365).toFixed(1)}y`;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 1 }}
      title={`${isEn ? "First docs published" : "白皮书/文档首次公开"}: ${entry.date} · ${entry.source}`}
    >
      <span style={{ fontSize: 11, fontWeight: 700, color: ageColor }}>
        📄 {entry.date}
        <DocSourceBadge sourceType={entry.sourceType} source={entry.source} url={entry.url} />
      </span>
      <span style={{ fontSize: 9, color: mutedColor }}>
        {ageLabel} {isEn ? "ago" : "前"}
      </span>
    </div>
  );
}
